// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the action chip (docs/konvo-assistant/action-chips.md, approved
// mockups in design/delegation-task). One component for every surface:
//
//  - `row`: the composer strip (C1, C2, P1): accent stub, the person's photo
//    with a ClickUp badge (or the kind icon), "Task for Markus" with a chevron
//    and quiet meta ("in iCAM Ticketing"), the task in one line underneath, a
//    ghost "+ Create task" button and ×;
//  - `pill`: the compact chip for messages (J1–J6): face, label ⌄, meta, a
//    filled + and ×.
//
// The label and chevron are one button that opens "Konvo can" (ActionChipMenu),
// which also offers the full prefilled form; + acts at once (ActionChipRun). After a
// create the chip says what it did with Open and Undo, then folds away. After
// a switch it may say "Next time, Gary's asks start as a task · Undo".
// A `meeting` chip ("Reply with a time") opens its time picker instead of the
// menu, from the label and from +.

import styles from '@app/features/konvo_assistant/components/action_chip/ActionChip.module.css';
import {ChipAvatar} from '@app/features/konvo_assistant/components/action_chip/ActionChipAvatar';
import {ActionChipMenu} from '@app/features/konvo_assistant/components/action_chip/ActionChipMenu';
import {
	actionLabel,
	chipDetail,
	chipLabel,
	chipMeta,
	doneMeta,
	doneTitle,
	learnedText,
	T,
} from '@app/features/konvo_assistant/components/action_chip/ActionChipText';
import {MeetingChipPanel} from '@app/features/konvo_assistant/components/action_chip/MeetingChipPanel';
import type {ActionChipController} from '@app/features/konvo_assistant/components/action_chip/state/ActionChipRun';
import type {ChoiceKey} from '@app/features/konvo_assistant/state/AssistantSuggestions';
import KonvoClickUp from '@app/features/messaging/state/KonvoClickUp';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {
	ArrowCounterClockwiseIcon,
	ArrowSquareOutIcon,
	CaretDownIcon,
	CheckIcon,
	PlusIcon,
	SparkleIcon,
	XIcon,
} from '@app/features/ui/icons/Icons';
import {Popout} from '@app/features/ui/popover/PopoverPopout';
import type {I18n} from '@lingui/core';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {useEffect, useRef} from 'react';

interface ActionChipProps {
	chip: ActionChipController;
	layout?: 'row' | 'pill';
	/** Finger-sized controls (the phone layout). */
	phone?: boolean;
	/** × was pressed (after the chip records it). */
	onDismiss?: () => void;
	/** The "Konvo can" menu opened or closed. */
	onMenu?: (open: boolean) => void;
}

/** "Konvo will offer less for Gary's asks · Undo", after "Nothing to do here" taught it something. */
function showQuietNote(i18n: I18n, chip: ActionChipController, learned: Parameters<typeof learnedText>[1]): void {
	ToastCommands.createToast({
		type: 'info',
		children: learnedText(i18n, learned),
		leading: <SparkleIcon size={16} />,
		action: {
			label: i18n._(T.undo),
			onClick: () => void chip.undoLearned(learned),
			'data-flx': 'konvo-assistant.action-chip.quiet-undo',
		},
		dismissLabel: i18n._(T.dismiss),
		timeout: 9000,
	});
}

export function pickChoice(i18n: I18n, chip: ActionChipController, key: ChoiceKey): void {
	void chip.select(key).then((learned) => {
		if (key === 'none' && learned) showQuietNote(i18n, chip, learned);
	});
}

export const ActionChip = observer(({chip, layout = 'row', phone = false, onDismiss, onMenu}: ActionChipProps) => {
	const {i18n} = useLingui();
	const chooseRef = useRef<HTMLButtonElement | null>(null);
	useEffect(() => {
		// The list names in the meta come from ClickUp's reference data.
		KonvoClickUp.prefetch();
	}, []);
	if (chip.phase === 'gone') return null;
	const pill = layout === 'pill';
	const done = chip.phase === 'done' || (chip.phase === 'busy' && chip.created != null);
	const dismiss = () => {
		onDismiss?.();
		chip.dismiss();
	};

	if (done) {
		return (
			<div className={clsx(styles.wrap, pill && styles.wrapPill)}>
				<div
					className={clsx(pill ? styles.pill : styles.row, phone && styles.phone, styles.done)}
					role="status"
					onMouseEnter={() => chip.holdFold(true)}
					onMouseLeave={() => chip.holdFold(false)}
					data-flx="konvo-assistant.action-chip.done"
				>
					<span className={clsx(styles.doneMark, pill && styles.doneMarkPill)} aria-hidden="true">
						<CheckIcon size={pill ? 12 : 16} weight="bold" />
					</span>
					<span className={styles.text}>
						<span className={styles.doneTitle}>{doneTitle(i18n, chip)}</span>
						{!pill && <span className={styles.detail}>{doneMeta(chip)}</span>}
					</span>
					<span className={styles.controls}>
						{chip.created?.id && (chip.created.key === 'task' || chip.created.key === 'task_me') && (
							<button
								type="button"
								className={styles.quiet}
								onClick={chip.open}
								data-flx="konvo-assistant.action-chip.open"
							>
								<ArrowSquareOutIcon size={15} />
								{i18n._(T.open)}
							</button>
						)}
						{chip.created?.id && (
							<button
								type="button"
								className={styles.quiet}
								disabled={chip.phase === 'busy'}
								onClick={() => void chip.undo(i18n)}
								data-flx="konvo-assistant.action-chip.undo"
							>
								<ArrowCounterClockwiseIcon size={15} />
								{i18n._(T.undo)}
							</button>
						)}
					</span>
				</div>
			</div>
		);
	}

	const key = chip.choice;
	const label = chipLabel(i18n, chip, key);
	const meta = chipMeta(i18n, chip, key);
	const busy = chip.phase === 'busy';
	const meeting = key === 'meeting';
	// + on a meeting chip opens its time picker (the label's popout), like the label does.
	const act = (target: HTMLElement) => {
		if (meeting) chooseRef.current?.click();
		else void chip.act(i18n, target);
	};
	// The label and its chevron are one button: anywhere on "Task for you ⌄" opens "Konvo can".
	// The prefilled form ("Edit before creating") is an entry in that menu.
	const choose = (
		<Popout
			position={pill ? 'bottom-start' : 'top-start'}
			offsetMainAxis={8}
			onOpen={() => onMenu?.(true)}
			onClose={() => onMenu?.(false)}
			render={({onClose}) =>
				meeting ? (
					<MeetingChipPanel chip={chip} onClose={onClose} />
				) : (
					<ActionChipMenu
						chip={chip}
						onClose={onClose}
						onPick={(next) => {
							onClose();
							pickChoice(i18n, chip, next);
						}}
						onEdit={() => {
							onClose();
							// The event card anchors to the chip, not to the closing menu.
							chip.openForm(chooseRef.current);
						}}
					/>
				)
			}
		>
			<button
				ref={chooseRef}
				type="button"
				className={styles.choose}
				title={meeting ? i18n._(T.pickTime) : i18n._(T.changeAction)}
				aria-haspopup={meeting ? 'dialog' : 'menu'}
				disabled={busy}
				data-flx="konvo-assistant.action-chip.label"
			>
				<span className={styles.label}>{label}</span>
				<CaretDownIcon size={pill && !phone ? 13 : 15} weight="bold" className={styles.caret} aria-hidden="true" />
			</button>
		</Popout>
	);
	const dismissButton = (
		<button
			type="button"
			className={styles.dismiss}
			aria-label={i18n._(T.dismiss)}
			onClick={dismiss}
			data-flx="konvo-assistant.action-chip.dismiss"
		>
			<XIcon size={pill ? 12 : 14} weight="bold" />
		</button>
	);
	// "Next time, …" belongs to its chip: it takes the row's second line (or sits under the pill),
	// rather than floating as a separate pill near the composer's corner.
	const learned = chip.learned && (
		<span className={styles.learned} role="status" data-flx="konvo-assistant.action-chip.learned">
			<SparkleIcon size={13} className={styles.learnedIcon} aria-hidden="true" />
			<span className={styles.learnedText}>{learnedText(i18n, chip.learned)}</span>
			<button
				type="button"
				className={styles.learnedUndo}
				onClick={() => void chip.undoLearned()}
				data-flx="konvo-assistant.action-chip.learned-undo"
			>
				{i18n._(T.undo)}
			</button>
		</span>
	);

	if (pill) {
		return (
			<div className={clsx(styles.wrap, styles.wrapPill)}>
				<div
					className={clsx(styles.pill, phone && styles.phone)}
					data-flx="konvo-assistant.action-chip"
					data-kind={key}
					aria-busy={busy}
				>
					<ChipAvatar chip={chip} choice={key} size="pill" />
					{choose}
					{meta && (
						<span className={styles.pillMeta} data-chip-meta="">
							{meta}
						</span>
					)}
					<button
						type="button"
						className={styles.plus}
						aria-label={actionLabel(i18n, key)}
						title={actionLabel(i18n, key)}
						disabled={busy}
						onClick={(event) => act(event.currentTarget)}
						data-flx="konvo-assistant.action-chip.create"
					>
						<PlusIcon size={14} weight="bold" />
					</button>
					{dismissButton}
				</div>
				{learned}
			</div>
		);
	}

	return (
		<div className={styles.wrap}>
			<div
				className={clsx(styles.row, phone && styles.phone)}
				data-flx="konvo-assistant.action-chip"
				data-kind={key}
				aria-busy={busy}
			>
				<ChipAvatar chip={chip} choice={key} size="row" />
				<span className={styles.text}>
					<span className={styles.lead}>
						{choose}
						{meta && !phone && <span className={styles.meta}>{meta}</span>}
					</span>
					{learned || (
						<span className={styles.detail} title={chip.suggestion.summary}>
							{/* P1: on a phone the meta leads the second line, so the label keeps its room. */}
							{phone && meta ? `${meta} · ${chipDetail(chip, key)}` : chipDetail(chip, key)}
						</span>
					)}
				</span>
				<span className={styles.controls}>
					<button
						type="button"
						className={styles.create}
						disabled={busy}
						onClick={(event) => act(event.currentTarget)}
						data-flx="konvo-assistant.action-chip.create"
					>
						<PlusIcon size={15} weight="bold" />
						{phone ? i18n._(T.create) : actionLabel(i18n, key)}
					</button>
					{dismissButton}
				</span>
			</div>
		</div>
	);
});
